import { Edit as EditIcon } from "@mui/icons-material";
import { IconButton, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { isEmpty, take } from "lodash";
import type { FC } from "react";
import { useState } from "react";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../../misc/useLimit";
import { useAdminFiltersValue } from "../useAdminFilters";
import type { ConfigFormState } from "./EditLoyaltyConfig";
import EditLoyaltyConfig from "./EditLoyaltyConfig";
import type { LoyaltyPointConfigResponse } from "./loyaltyApi";
import { useLoyaltyPointConfigs } from "./useLoyaltyPointConfigs";

const LoyaltyPage = () => {
  const { limit, loadMore } = useLimit();
  const { loyaltyPointConfigs, isLoading, updateLoyaltyPointConfig } = useLoyaltyPointConfigs();

  const { query } = useAdminFiltersValue("loyalty");

  const filteredLoyaltyPointConfigs = (loyaltyPointConfigs ?? []).filter(
    (p) => isEmpty(query) || p.action.toLowerCase().includes(query),
  );

  return (
    <Stack
      sx={{
        height: "100%",
      }}
    >
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < filteredLoyaltyPointConfigs.length ? limit : filteredLoyaltyPointConfigs.length}
        hasMore={limit < filteredLoyaltyPointConfigs.length}
        fetchData={loadMore}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Akcja</TableCell>
              <TableCell>Punkty</TableCell>
              <TableCell>Włączony</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {take(filteredLoyaltyPointConfigs, limit).map((p) => (
              <LoyaltyConfigRow
                key={p.action}
                loyaltyConfig={p}
                onConfigChange={async (config: ConfigFormState) => {
                  await updateLoyaltyPointConfig({
                    action: p.action,
                    staticConfig: config.staticConfig,
                    dynamicConfig: config.dynamicConfig
                      ? { multiplier: config.dynamicConfig.multiplier.toString() }
                      : null,
                    enabled: config.enabled,
                  });
                }}
              />
            ))}
          </TableBody>
        </Table>
      </InfiniteScroll>
    </Stack>
  );
};

interface LoyaltyConfigRowProps {
  readonly loyaltyConfig: LoyaltyPointConfigResponse;
  readonly onConfigChange: (config: ConfigFormState) => Promise<void>;
}

const LoyaltyConfigRow: FC<LoyaltyConfigRowProps> = ({ loyaltyConfig, onConfigChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const isDynamic = !loyaltyConfig.staticConfig;
  const configLabel = isDynamic ? "Dynamiczne: x " : "Statyczne: ";
  const configValue = loyaltyConfig.staticConfig?.points ?? loyaltyConfig.dynamicConfig?.multiplier;
  return (
    <TableRow data-testid="loyalty-config-row">
      <TableCell>{loyaltyConfig.action}</TableCell>
      <TableCell>{configLabel + configValue}</TableCell>
      <TableCell>{loyaltyConfig.enabled ? "🟢" : "🔴 "}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={2}>
          <IconButton size="small" aria-label="Edytuj akcje" onClick={() => setDialogOpen(true)}>
            <EditIcon />
          </IconButton>
        </Stack>
        <EditLoyaltyConfig
          open={dialogOpen}
          action={loyaltyConfig.action}
          description={loyaltyConfig.description}
          config={{
            staticConfig: loyaltyConfig.staticConfig,
            dynamicConfig: loyaltyConfig.dynamicConfig
              ? { multiplier: Number(loyaltyConfig.dynamicConfig.multiplier) }
              : null,
            enabled: loyaltyConfig.enabled,
          }}
          onChange={onConfigChange}
          onClose={() => setDialogOpen(false)}
        />
      </TableCell>
    </TableRow>
  );
};

export default LoyaltyPage;
